import { Component, type ErrorInfo, type ReactNode } from 'react';
import { t } from '@/i18n';
import { fehlerberichteBeobachten, meldeFehler } from '@/lib/fehlerbericht';

/**
 * Auffangnetz für Render-Fehler. Ohne das war ein Fehler mitten im Turnier eine
 * **weiße Seite** — ohne Text, ohne Knopf, bei einer Anwendung, deren Daten nur
 * auf diesem Gerät liegen. Hier steht, was passiert ist, welcher Stand läuft,
 * und ein Weg zurück. Die Daten sind nicht weg: Sie liegen in IndexedDB,
 * ein Neuladen holt sie wieder.
 */
/** Die ersten drei Stack-Zeilen ohne die Meldung selbst — genug für „welcher Chunk, welche Zeile" */
function stackKurz(error: Error): string {
  const zeilen = (error.stack ?? '')
    .split('\n')
    .filter((z) => !z.includes(error.message))
    .slice(0, 3)
    .map((z) => z.trim());
  return zeilen.length ? `\n${zeilen.join('\n')}` : '';
}

export class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Render-Fehler', error, info.componentStack);
    // Einmal an den Server (docs/notes/betrieb.md) — ohne Turnierdaten, ohne zu warten, nie werfend
    meldeFehler(error, 'render');
  }

  /*
   * Ein Seitenwechsel (Zurück-Taste, Link) räumt den Fehler weg — vorher blieb
   * die Meldung stehen, bis man neu lud, und „Zurück" wirkte kaputt (Daniel,
   * 09/2026, Impressum). Der Zustand der App ist davon unberührt; nur der
   * Baum wird neu gezeichnet.
   */
  private readonly zuruecksetzen = () => {
    if (this.state.error) this.setState({ error: null });
  };

  override componentDidMount() {
    window.addEventListener('hashchange', this.zuruecksetzen);
    // Unbehandelte Promise-Ablehnungen kommen nie hier an — sie werden nur gemeldet
    fehlerberichteBeobachten();
  }

  override componentWillUnmount() {
    window.removeEventListener('hashchange', this.zuruecksetzen);
  }

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="max-w-lg mx-auto p-8 text-sm">
        <h1 className="text-xl font-bold m-0 mb-2">{t('Da ist etwas schiefgegangen')}</h1>
        <p className="text-muted m-0 mb-4">
          {t('Die Seite konnte nicht gezeichnet werden. Deine Daten liegen weiterhin auf diesem Gerät — ein Neuladen holt sie wieder. Bleibt der Fehler, bitte den Text unten an den Support geben.')}
        </p>
        {/* Die ersten Zeilen des Stacks nennen die Datei — ohne sie war „l is not a function" nicht zuzuordnen */}
        <pre className="bg-panel2 border border-line p-3 text-xs overflow-auto whitespace-pre-wrap m-0 mb-4">
          {error.message}
          {'\n'}
          {t('Version')} {__APP_VERSION__} · {navigator.userAgent}
          {'\n'}
          {location.hash}
          {stackKurz(error)}
        </pre>
        <div className="flex gap-2 flex-wrap">
          <button className="border border-line bg-raise px-3 py-2 font-bold cursor-pointer" onClick={() => location.reload()}>
            {t('Neu laden')}
          </button>
          <a href="#/" className="border border-line px-3 py-2 no-underline text-ink" onClick={() => this.setState({ error: null })}>
            {t('Zur Turnierliste')}
          </a>
        </div>
      </div>
    );
  }
}
